
// File: components/engine/systems/spawner.ts
import { COLORS } from "../../config/colors";
import { CANVAS_WIDTH, ENEMY_WIDTH, ENEMY_HEIGHT, PLAYER_WIDTH } from "../../config/dimensions";
import { Enemy } from "../../types/entities";
import { GameInstance, MAX_WAVES } from "../GameInstance";

export const spawnEnemy = (game: GameInstance, x: number, hp?: number, style?: number) => {
  // Force style 11 (Dummy) if practice mode
  const actualStyle = game.isPracticeMode ? 11 : (style ?? Math.floor(Math.random() * 5));

  game.enemies.push({
    x, 
    y: 60, 
    width: ENEMY_WIDTH, 
    height: ENEMY_HEIGHT, 
    color: COLORS.enemy,
    hp: hp || (3 + Math.floor(game.wave / 3)),
    moveTimer: Math.random() * 100,
    targetX: x,
    flashTimer: 0,
    style: actualStyle,
    teleportTimer: Math.random() * 100 // Initialize teleport timer
  });
};

const spawnPlaneWave = (game: GameInstance, wave: number) => {
    const centerX = CANVAS_WIDTH / 2 - PLAYER_WIDTH / 2;
    if (wave === 2) {
        // Wave 2: More enemies, tougher variants
        spawnEnemy(game, centerX - 300, 4, 7); // Male Staff
        spawnEnemy(game, centerX + 300, 4, 6); // Female Staff
        spawnEnemy(game, centerX - 150, 5, 7); // Male Staff
        spawnEnemy(game, centerX + 150, 5, 6); // Female Staff
        spawnEnemy(game, centerX, 8, 5);       // A mini-pilot
    } else if (wave === 3) {
        // Wave 3: Final wave with a boss
        spawnEnemy(game, centerX, 20, 5);      // The BIG pilot again, more HP
        spawnEnemy(game, centerX - 250, 6, 7); // Elite Male Staff
        spawnEnemy(game, centerX + 250, 6, 6); // Elite Female Staff
        spawnEnemy(game, centerX - 100, 6, 7); // Elite Male Staff
        spawnEnemy(game, centerX + 100, 6, 6); // Elite Female Staff
    }
};

const spawnBathhouseWave = (game: GameInstance, wave: number) => {
    const centerX = CANVAS_WIDTH / 2 - PLAYER_WIDTH / 2;
    
    // Helper to get random water demon style
    const randomWaterStyle = () => {
        const roll = Math.random();
        if (roll < 0.33) return 8; // Standard
        if (roll < 0.66) return 9; // Heavy
        return 10; // Swift
    };

    if (wave === 2) {
        // Wave 2: The Saja Boys Arrive (Ambush)
        // Baby Saja (Style 1) and Abs Saja (Style 2)
        spawnEnemy(game, centerX - 200, 8, 1); 
        spawnEnemy(game, centerX + 200, 8, 2); 
        // Random Water Demon
        spawnEnemy(game, centerX, 6, randomWaterStyle()); 
    } else if (wave === 3) {
        // Wave 3: Jinu (Leader) & Romance Saja
        // Jinu (Style 4 - Mystery)
        spawnEnemy(game, centerX, 30, 4); 
        // Romance Saja (Style 3)
        spawnEnemy(game, centerX - 150, 10, 3);
        // Random Water Demon
        spawnEnemy(game, centerX + 150, 6, randomWaterStyle()); 
    }
};

export const spawnAllyAttack = (game: GameInstance, type: 'MIRA_WAVE' | 'ZOEY_SHOCKWAVE') => {
    const enemies = game.enemies;

    if (type === 'MIRA_WAVE') {
        const originX = game.mira.x + game.mira.width / 2;
        const originY = game.mira.y;
        
        let angle = -Math.PI / 2; 

        if (enemies.length > 0) {
             const target = enemies[Math.floor(Math.random() * enemies.length)];
             const tx = target.x + target.width / 2;
             const ty = target.y + target.height / 2;
             angle = Math.atan2(ty - originY, tx - originX) + (Math.random() - 0.5) * 0.2;
        } else {
             angle = -Math.PI + (Math.random() * Math.PI);
        }

        if (angle > -0.2) angle = -0.2;
        if (angle < -Math.PI + 0.2) angle = -Math.PI + 0.2;

        const speed = 9;

        game.allyProjectiles.push({
            type: 'MIRA_WAVE',
            x: originX - 60, 
            y: originY - 20,
            width: 120, 
            height: 40,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            color: '#ec4899',
            life: 80,
        });
    } 
    else if (type === 'ZOEY_SHOCKWAVE') {
        const originX = game.zoey.x + game.zoey.width / 2;
        const originY = game.zoey.y;

        for (let i = 0; i < 3; i++) {
            let angle = -Math.PI / 2;
            
            if (enemies.length > 0) {
                const target = enemies[Math.floor(Math.random() * enemies.length)];
                const tx = target.x + target.width / 2;
                const ty = target.y + target.height / 2;
                angle = Math.atan2(ty - originY, tx - originX) + (Math.random() - 0.5) * 0.8; 
            } else {
                angle = -Math.PI / 2 + (Math.random() - 0.5) * 2.0;
            }
            
            if (angle > 0) angle = -Math.PI / 2 + (Math.random() - 0.5); 

            const speed = 12 + Math.random() * 4;

            game.allyProjectiles.push({
                type: 'ZOEY_MISSILE',
                x: originX - 10,
                y: originY,
                width: 20, 
                height: 40,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                color: '#06b6d4', 
                life: 60,
            });
        }
    }
};

export const spawnProjectile = (game: GameInstance, e: Enemy) => {
  const startX = e.x + e.width / 2;
  const startY = e.y + e.height / 2;
  
  const angle = Math.atan2(game.player.y - startY, game.player.x - startX);
  
  // Base properties
  let speed = 4 + Math.random() * 2;
  let type: "DARK" | "LIGHT" | "GOLD" | "SONIC" | "BEAM" | "HEART" = "DARK";
  let radius = 8;

  // Customize based on Style
  switch (e.style) {
      case 1: // Baby Saja: Sonic Wave
          type = "SONIC";
          speed = 3; // Slower
          radius = 20; // Larger hit area
          break;
      case 2: // Abs Saja: Flash Beam
          type = "BEAM";
          speed = 12; // Very fast
          radius = 6;
          break;
      case 3: // Romance Saja: Heart
          type = "HEART";
          speed = 4;
          radius = 12;
          break;
      case 4: // Mystery Saja
          type = "DARK";
          speed = 5;
          break;
      case 8: // Water Demon Standard
      case 9: // Water Demon Heavy (Slower, harder hit implied by game logic later?)
          type = "DARK";
          speed = 4;
          break;
      case 10: // Water Demon Swift
          type = "DARK";
          speed = 7; // Faster projectile
          break;
      case 11: // Practice Dummy
          type = "LIGHT"; // Shoot non-harmful or just simple dark? Let's use DARK but slow
          speed = 2;
          radius = 5;
          break;
      default: // Style 0 & others: Standard
          type = "DARK";
          speed = 4 + Math.random() * 2;
          break;
  }

  game.projectiles.push({
    x: startX, y: startY,
    vx: Math.cos(angle) * speed, 
    vy: Math.sin(angle) * speed,
    radius, 
    type, 
    active: true,
    initialX: startX, // Used for tracking/sine patterns
    time: 0
  });
};

export const spawnParticles = (game: GameInstance, x: number, y: number, color: string, count: number) => {
  for(let i=0; i<count; i++) {
    game.particles.push({
      x, y, vx: (Math.random()-0.5)*10, vy: (Math.random()-0.5)*10, life: 1.0, color
    });
  }
};

export const handleSpawning = (game: GameInstance) => {
  game.spawnTimer++;
  // Spawn rate increases as waves progress (enemies shoot faster)
  // BUGFIX: Use planeWave for spawn rate scaling on plane stage
  const currentWave = game.isPlaneStage ? game.planeWave : game.wave;
  const spawnRate = Math.max(20, 100 - currentWave * 8);
  
  if (game.spawnTimer > spawnRate && game.enemies.length > 0) {
    game.spawnTimer = 0;
    // Pick a random shooter
    const shooter = game.enemies[Math.floor(Math.random() * game.enemies.length)];
    spawnProjectile(game, shooter);
  }

  // Wave Cleared?
  if (game.enemies.length === 0 && !game.isGameOver) {
    if (game.isPlaneStage) {
      // There are 3 waves total. planeWave is 1-indexed.
      // After wave 3 (planeWave=3) is cleared, victory.
      if (game.planeWave >= 3) {
        game.isGameOver = true;
        game.callbacks.onVictory(game.score);
      } else {
        game.planeWave++;
        spawnPlaneWave(game, game.planeWave);
        // CRITICAL FIX: If the next wave doesn't spawn any enemies for any reason,
        // this would cause an infinite loop in the next frame. We must check
        // if enemies were actually spawned and trigger victory if not.
        if (game.enemies.length === 0) {
            game.isGameOver = true;
            game.callbacks.onVictory(game.score);
        }
      }
    } else if (game.isBathhouseFight) {
        // Bathhouse has 3 specialized waves
        if (game.wave >= 3) {
            game.isGameOver = true;
            game.callbacks.onVictory(game.score);
        } else {
            game.wave++;
            spawnBathhouseWave(game, game.wave);
            if (game.enemies.length === 0) {
                 game.isGameOver = true;
                 game.callbacks.onVictory(game.score);
            }
            game.callbacks.onUpdateUI(game.score, game.honmoon, game.wave, game.goldenHonmoonTimer);
        }
    } else if (game.wave < MAX_WAVES) {
      game.wave++;
      game.callbacks.onUpdateUI(game.score, game.honmoon, game.wave, game.goldenHonmoonTimer);
      
      const count = Math.min(5, 2 + Math.floor((game.wave - 1) / 2)); 
      // If practice mode, force style 11 logic is in spawnEnemy, just pass whatever here
      const styles = [0, 1, 2, 3, 4].sort(() => 0.5 - Math.random());
      const spacing = CANVAS_WIDTH / (count + 1);
      const waveHp = 3 + Math.floor(game.wave / 3);

      for(let i=0; i<count; i++) {
        const style = styles[i % styles.length];
        const jitter = (Math.random() - 0.5) * 50;
        spawnEnemy(game, (spacing * (i + 1) - PLAYER_WIDTH/2) + jitter, waveHp, style);
      }
    } else if (game.wave >= MAX_WAVES) {
      game.isGameOver = true;
      game.callbacks.onVictory(game.score);
    }
  }
};
